import { createTheme } from '@mui/material/styles';
import { tokens } from './tokens';

const { surface, line, ink, accent, signal, radius, shadow, font } = tokens;

/**
 * Direct-child selectors for the trailing cell of every row in a
 * `TableContainer`'s table. Direct children only, so a table nested inside a
 * cell is left alone.
 */
const LAST_HEAD_CELL =
  '& > .MuiTable-root > .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-root:last-child:not(:first-child)';
const LAST_BODY_CELL =
  '& > .MuiTable-root > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root:last-child:not(:first-child)';

export const appTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: accent.main,
      light: '#60a5fa',
      dark: accent.hover,
      contrastText: ink.inverse,
    },
    secondary: {
      main: '#0f766e',
      light: '#14b8a6',
      dark: '#115e59',
      contrastText: '#ffffff',
    },
    background: {
      // The one page background. The app shell paints it behind every
      // signed-in page; pages inherit it rather than declaring their own.
      default: surface.page,
      paper: surface.card,
    },
    text: {
      primary: ink.primary,
      secondary: ink.bodyAlt,
    },
    divider: line.default,
    // The muted signal set from theme/tokens.ts, app-wide. `main` is the ink
    // tone so text and outlined chips clear contrast. `light`/`dark` are left
    // to MUI's usual derivation so stock Alert and Chip keep their
    // conventions; dots and bars read `tokens.signal[tone].solid` directly.
    success: {
      main: signal.positive.ink,
      contrastText: ink.inverse,
    },
    warning: {
      main: signal.caution.ink,
      contrastText: ink.inverse,
    },
    error: {
      main: signal.problem.ink,
      contrastText: ink.inverse,
    },
    info: {
      main: signal.info.ink,
      contrastText: ink.inverse,
    },
    grey: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
  },
  shape: {
    borderRadius: radius.card,
  },
  typography: {
    fontFamily: font.sans,
    h1: { fontSize: '2.5rem', fontWeight: 700, lineHeight: 1.2 },
    h2: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.25 },
    h3: { fontSize: '1.625rem', fontWeight: 700, lineHeight: 1.3 },
    h4: { fontSize: '1.375rem', fontWeight: 600, lineHeight: 1.35 },
    h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.35 },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55 },
    caption: { fontSize: '0.75rem', lineHeight: 1.5 },
    // The eyebrow label pages used to hand-build from caption + uppercase +
    // letter spacing. Use `variant="overline"` instead.
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      lineHeight: 1.6,
    },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: surface.page,
          color: ink.primary,
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        // MUI 7 Snackbar is position:fixed and does not portal. Dialogs do.
        // Keep toasts above modal/drawer/appBar (and nested dialogs).
        root: {
          zIndex: 10000,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: radius.card,
          border: `1px solid ${line.default}`,
          boxShadow: shadow.card,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radius.card,
          border: `1px solid ${line.default}`,
          boxShadow: shadow.card,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.control,
          paddingInline: 14,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiTabs: {
      // Tab strips scroll instead of clipping on a phone. A page that needs
      // `centered` must pass `variant="standard"` explicitly, because MUI
      // warns on `centered` together with `scrollable`.
      defaultProps: {
        variant: 'scrollable',
        scrollButtons: 'auto',
        allowScrollButtonsMobile: true,
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.control,
          backgroundColor: surface.card,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          color: ink.body,
          backgroundColor: surface.sunken,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        // A wide table scrolls inside its own container instead of overflowing
        // the card or the page (MOT-84). `minWidth: 0` lets the container shrink
        // when it is a flex/grid child.
        //
        // The last column (Actions / Status) is pinned to the right edge with an
        // opaque background and a soft left shadow, so it stays reachable while
        // the other columns scroll underneath it.
        root: ({ theme }) => ({
          overflowX: 'auto',
          minWidth: 0,
          maxWidth: '100%',
          [LAST_HEAD_CELL]: {
            position: 'sticky',
            right: 0,
            zIndex: 2,
            whiteSpace: 'nowrap',
            backgroundColor: surface.sunken,
            boxShadow: '-8px 0 8px -8px rgba(15,23,42,.18)',
          },
          [LAST_BODY_CELL]: {
            position: 'sticky',
            right: 0,
            zIndex: 1,
            whiteSpace: 'nowrap',
            backgroundColor: surface.card,
            boxShadow: '-8px 0 8px -8px rgba(15,23,42,.18)',
          },
          // A sticky cell is opaque, so the row's hover and selected washes are
          // layered on top of it as a gradient rather than a transparent colour.
          [`& > .MuiTable-root > .MuiTableBody-root > .MuiTableRow-hover:hover > .MuiTableCell-root:last-child:not(:first-child)`]:
            {
              backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
            },
          [`& > .MuiTable-root > .MuiTableBody-root > .MuiTableRow-root.Mui-selected > .MuiTableCell-root:last-child:not(:first-child)`]:
            {
              backgroundImage: `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected})`,
            },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radius.small,
          fontWeight: 600,
        },
        sizeSmall: {
          height: 24,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: `1px solid ${line.default}`,
        },
      },
    },
  },
});
